import { type FC, type ReactNode } from 'react';
import {
    vettedSeller,
    goldSeller,
    platinumSeller,
    diamondSeller,
    recognizedTitle,
    recognizedSeller,
    recognizedDescription,
    goldTooltip,
    platinumTooltip,
    diamondTooltip,
    vettedTooltip,
} from '../../messages/pdpDistinctionsMessages';
import { graphql, useFragment } from 'react-relay';

import Recognition from 'dibs-icons/exports/Recognition';
import Diamond from 'dibs-icons/exports/Diamond';
import Medal from 'dibs-icons/exports/Medal';
import Vetting from 'dibs-icons/exports/Vetting';

// styles
import styles from './PdpSharedSellerDistinction.scss';
import classNames from 'classnames';
import dibsCss from 'dibs-css';

// types
import { type PdpSharedSellerDistinction_seller$key as Seller } from './__generated__/PdpSharedSellerDistinction_seller.graphql';

type Props = {
    seller: Seller;
    hideDescription?: boolean;
    useDistinguishedShortTitle?: boolean;
    isCompact?: boolean;
};

type CreateDealerDistinctionProps = {
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
    hideDescription: boolean;
};

const createDealerDistinction = ({
    icon,
    title,
    description,
    hideDescription,
}: CreateDealerDistinctionProps): ReactNode => (
    <>
        <div className={styles.distinction}>
            {icon}
            <span className={styles.title}>{title}</span>
        </div>
        {!hideDescription && <div className={styles.description}>{description}</div>}
    </>
);

const sellerFragment = graphql`
    fragment PdpSharedSellerDistinction_seller on Seller {
        isDistinguished
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        sellerTiers {
            isGoldTier
            isPlatinumTier
            sellerTierDataNotFound
        }
    }
`;

export const PdpSharedSellerDistinction: FC<Props> = ({
    seller: sellerRef,
    hideDescription = false,
    useDistinguishedShortTitle = false,
    isCompact = false,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const sellerTiers = seller?.sellerTiers;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const isDistinguished = seller?.isDistinguished;
    const sellerTierDataFound = sellerTiers?.sellerTierDataNotFound;
    const isGoldSeller = !sellerTierDataFound && sellerTiers?.isGoldTier;
    const isPlatinumSeller = !sellerTierDataFound && sellerTiers?.isPlatinumTier;
    const distinctions = [];

    if (isDistinguished) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={classNames(styles.icon, dibsCss.fillSuccesssecondary)}>
                        <Recognition variation="20" />
                    </span>
                ),
                title: useDistinguishedShortTitle ? recognizedTitle : recognizedSeller,
                description: recognizedDescription,
                hideDescription,
            })
        );
    }

    if (isGoldSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={classNames(styles.icon, styles.goldDiamond)}>
                        <Medal variation="20" />
                    </span>
                ),
                title: goldSeller,
                description: goldTooltip,
                hideDescription,
            })
        );
    } else if (isPlatinumSeller && !isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={classNames(styles.icon, styles.platinumDiamond)}>
                        <Medal variation="20" />
                    </span>
                ),
                title: platinumSeller,
                description: platinumTooltip,
                hideDescription,
            })
        );
    } else if (isRewardsTier2) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={classNames(styles.icon)}>
                        <Diamond variation="20" />
                    </span>
                ),
                title: diamondSeller,
                description: diamondTooltip,
                hideDescription,
            })
        );
    } else if (!distinctions.length) {
        distinctions.push(
            createDealerDistinction({
                icon: (
                    <span className={classNames(styles.icon, dibsCss.fillShagreen800)}>
                        <Vetting variation="20" />
                    </span>
                ),
                title: vettedSeller,
                description: vettedTooltip,
                hideDescription,
            })
        );
    }

    return (
        <>
            {distinctions.map((distinction, index) => (
                <div
                    className={classNames({
                        [dibsCss.mbSmaller]: isCompact,
                        [dibsCss.mbXsmall]: !isCompact,
                    })}
                    key={`distinction-${index}`}
                >
                    {distinction}
                </div>
            ))}
        </>
    );
};
